import { Localized } from "@fluent/react";
import {
    Dialog, DialogActions, DialogContent, DialogTitle, TextField, MenuItem,
} from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { AppId } from "../../../../app/appTypes";
import { ProjectProcess, updateProjectProcessName } from "../../projectProcess/ProjectProcessApiClient";
import { useAppDispatch } from "../../../../app/hooks";
import { SaveButton } from "../../../../app/components/SaveButton";
import { CancelButton } from "../../../../app/components/CancelButton";
import { ValidationScenario } from "../validationProcessApi";
import { showSuccess } from "../../../notifications/notificationsSlice";


export interface EditProcessDescriptionFieldProps {
    processId: AppId;
    field: "no" | "name" | "description" | "generalContractor" | "investor" | "scenario" | string;
    value: string;
    onSave: (project: ProjectProcess) => void;
    onCancel: () => void;
}

const Label = ({ fieldName }: { fieldName: string }) => {
    switch (fieldName) {
        case "no": return <Localized id="process-no">Numer procesu</Localized>;
        case "name": return <Localized id="process-name">Nazwa procesu</Localized>;
        case "description": return <Localized id="process-description">Opis</Localized>;
        case "generalContractor": return <Localized id="project-general-contractor">Generalny wykonawca</Localized>;
        case "investor": return <Localized id="project-investor">Inwestor</Localized>;
        case "scenario": return <Localized id="process-scenario">Scenariusz</Localized>;
        default: return <>{fieldName}</>;
    }
};

const SCENARIO_OPTIONS: { value: ValidationScenario; label: React.ReactNode }[] = [
    { value: "NEW", label: <Localized id="scenario-new">Walidacja nowego obiektu</Localized> },
    { value: "REQUAL_PERIODIC", label: <Localized id="scenario-requal-periodic">Rekwalifikacja okresowa</Localized> },
    { value: "REQUAL_CHANGE", label: <Localized id="scenario-requal-change">Rekwalifikacja po zmianach</Localized> },
];

export const EditProcessDescriptionField = (props: EditProcessDescriptionFieldProps) => {
    const dispatch = useAppDispatch();
    const { processId, field, value } = props;

    const [newValue, setNewValue] = useState(value ?? "");
    const [saving, setSaving] = useState(false);
    const isScenario = field === "scenario";

    useEffect(() => {
        if (field === "scenario") {
            setNewValue((value as ValidationScenario) || "new");
        } else {
            setNewValue(value ?? "");
        }
    }, [isScenario, value, processId, field]);


    const handleOkClick = () => {
        setSaving(true);
        dispatch(
            updateProjectProcessName({
                id: processId,
                data: { fieldName: field, text: newValue },
            })
        )
            .then(unwrapResult)
            .then(project => props.onSave(project))
            .catch(console.error)
            .finally(() => setSaving(false));
    };

    return (
        <Dialog open fullWidth maxWidth="sm">
            <DialogTitle
                sx={{
                    bgcolor: (theme) => theme.palette.grey[200],
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    py: 0.75,
                }}
            >
                <Localized id="edit">Edycja</Localized>
            </DialogTitle>

            <DialogContent sx={{ mt: 2 }}>
                {isScenario ? (
                    <TextField
                        sx={{ mt: 1 }}
                        fullWidth
                        select
                        size="small"
                        label={<Label fieldName={field} />}
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value as ValidationScenario)}
                        SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
                    >
                        {SCENARIO_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </TextField>
                ) : (
                    <TextField
                        sx={{ mt: 1 }}
                        fullWidth
                        size="small"
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        label={<Label fieldName={field} />}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} onClick={handleOkClick} />
                <CancelButton loading={saving} onClick={props.onCancel} />
            </DialogActions>
        </Dialog >
    );
};
